import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { formatDateTime } from '../lib/format';
import { preset, rangeQuery, type RangeValue } from '../lib/range';
import { EmptyState, ErrorNote, LoadMore, Panel, Spinner } from '../components/ui';
import { RangePicker } from '../components/RangePicker';
import { DocumentViewer, type DocumentRequest } from '../components/DocumentViewer';

/**
 * Readings.
 *
 * ── What is deliberately *not* here ───────────────────────────────────────
 *
 * Receipts. There was briefly a single index of every document the till has ever
 * produced, and it was the wrong shape: nobody goes looking for "a document",
 * they go looking for the receipt for a sale, the invoice for a delivery, the
 * receipt for a payment somebody is querying on the phone. So each receipt is
 * reached from the thing it documents — sales and refunds on Sales, pay-ins and
 * pay-outs on Cash, invoices on Purchases, settlements under the account that
 * made them.
 *
 * The four range reports, which used to sit above the readings, are gone too.
 *
 * What is left is the pair of things with no other home: an X reading over a
 * window the till cannot ask for, and the Z readings it printed and uploaded.
 * Both are documents rather than pages, so both open in the viewer.
 */

interface ZReading {
  documentId: number;
  tillId: number;
  tillName: string;
  at: string;
}

interface Till {
  tillId: number;
  name: string;
}

export function Documents(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const timezone = activeStore?.timezone ?? 'Europe/London';

  const [range, setRange] = useState<RangeValue>(preset('last30'));
  const [viewing, setViewing] = useState<DocumentRequest | null>(null);

  const { data: reference } = useQuery({
    queryKey: ['reference', storeId],
    queryFn: () => api.get<{ tills: Till[] }>(`/stores/${storeId}/reference`),
    enabled: Boolean(storeId),
    staleTime: 5 * 60_000,
  });

  // Paginated: dev01 has 100 Z readings and the old hard limit showed 50 of
  // them, with nothing to say the list had stopped early.
  const {
    data: readingPages,
    error,
    isPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['readings', storeId],
    queryFn: ({ pageParam }) =>
      api.get<{ readings: ZReading[]; nextCursor: string | null }>(
        `/stores/${storeId}/documents/readings?limit=50` +
          `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(storeId),
  });

  const readings = readingPages?.pages.flatMap((page) => page.readings) ?? [];

  // `GET /documents/coverage` is no longer read here — the line explaining why
  // an old receipt 404s was removed. The endpoint stays: it is a real
  // diagnostic and the smoke suite still checks it.

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Documents</p>
          <h1 className="text-lg font-medium tracking-tight">Readings</h1>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      {/* An X reading over an arbitrary window is a cloud-only capability: the
          till can only ever take one for the period since its last Z. */}
      {(reference?.tills.length ?? 0) > 0 && (
        <section className="mb-6">
          <h2 className="qs-eyebrow mb-2">X reading</h2>

          <Panel className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Gauge size={15} className="shrink-0 text-ink-faint" aria-hidden />
              {reference?.tills.map((till) => (
                <button
                  key={till.tillId}
                  type="button"
                  onClick={() =>
                    setViewing({
                      path: `/stores/${storeId}/documents/x-reading/${till.tillId}?${rangeQuery(range)}`,
                      title: `X reading — ${till.name}`,
                      formats: ['text', 'pdf'],
                    })
                  }
                  className="rounded border border-rule px-2.5 py-1 text-2xs text-ink-muted hover:border-accent hover:text-accent"
                >
                  {till.name}
                </button>
              ))}
            </div>
          </Panel>
        </section>
      )}

      <section>
        <h2 className="qs-eyebrow mb-2">Z readings</h2>

        {error && <ErrorNote error={error} />}

        <Panel>
          {isPending ? (
            <Spinner label="Loading readings…" />
          ) : readings.length === 0 ? (
            <EmptyState>No Z readings have been taken on this store.</EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <caption className="sr-only">Recorded Z readings</caption>
                <thead>
                  <tr className="border-b border-rule">
                    <th className="qs-eyebrow py-2 pl-3 text-left">Taken</th>
                    <th className="qs-eyebrow py-2 text-left">Till</th>
                    <th className="qs-eyebrow py-2 pr-3 text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {readings.map((reading) => (
                    <tr key={reading.documentId} className="border-b border-rule last:border-0">
                      <td className="qs-figure py-1.5 pl-3 text-xs">
                        {formatDateTime(reading.at, timezone)}
                      </td>
                      <td className="py-1.5 text-xs text-ink-muted">{reading.tillName}</td>
                      <td className="py-1.5 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setViewing({
                              path: `/stores/${storeId}/documents/reading/${reading.documentId}`,
                              title: `Z reading — ${reading.tillName}`,
                              // Stated rather than left to the default: the till
                              // archives a reading twice, as a Text row and a
                              // PDF row, so unlike every other receipt here the
                              // format button genuinely fetches a different
                              // file.
                              formats: ['text', 'pdf'],
                            })
                          }
                          className="rounded border border-rule px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <LoadMore
            shown={readings.length}
            hasMore={Boolean(hasNextPage)}
            isLoading={isFetchingNextPage}
            onLoad={() => void fetchNextPage()}
            noun="readings shown"
          />
        </Panel>
      </section>

      <DocumentViewer request={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
